import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, Store, Tag, Eye, MousePointer, Ticket, Clock, AlertTriangle, TrendingUp, Handshake, Inbox, Euro, Repeat, UserCheck, CreditCard, Send } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";
import AdminAlertsStats from "@/pages/admin/AdminAlertsStats";

const slug = (s) => (s || "")
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const fmtEur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  const [trials, setTrials] = useState([]);
  const [inv, setInv] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  useEffect(() => { api.get("/admin/trials/ending-soon").then((r) => setTrials(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/invites/stats").then((r) => setInv(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="text-slate-500">Chargement…</div>;

  const primary = [
    {label: "Nouvelles demandes",   value: s.new_requests || 0,     icon: Inbox,        color: "text-rose-600",    to: "/admin/demandes"},
    {label: "Publications FB",      value: s.fb_posts_pending || 0, icon: AlertTriangle,color: "text-blue-600",    to: "/admin/publications-facebook"},
    {label: "Demandes urgentes",    value: s.urgent_requests || 0,  icon: AlertTriangle,color: "text-orange-600",  to: "/admin/demandes"},
    {label: "Prospects CRM",        value: s.prospects || 0,        icon: TrendingUp,   color: "text-blue-600",    to: "/admin/professionnels"},
    {label: "Partenaires actifs",   value: s.partners || 0,         icon: Handshake,    color: "text-emerald-600", to: "/admin/professionnels"},
    {label: "Offres expirant (7j)", value: s.expiring_offers || 0,  icon: Clock,        color: "text-amber-600",   to: "/admin/offres"},
    {label: "Offres à modérer",     value: s.offers_pending || 0,   icon: Tag,          color: "text-purple-600",  to: "/admin/offres"},
  ];
  const global = [
    {label: "Utilisateurs",      value: s.users, icon: Users},
    {label: "Professionnels",    value: s.professionals, icon: Store},
    {label: "Offres publiées",   value: s.offers_active, icon: Tag},
    {label: "Total offres",      value: s.offers_total, icon: Tag},
    {label: "Vues",              value: s.views, icon: Eye},
    {label: "Clics",             value: s.clicks, icon: MousePointer},
    {label: "Utilisations",      value: s.redemptions, icon: Ticket},
  ];

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold" data-testid="admin-dashboard-title">Dashboard</h1>

      <h2 className="mt-6 text-sm font-bold uppercase tracking-widest text-slate-500">À traiter</h2>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {primary.map((c) => (
          <Link key={c.label} to={c.to}
                className="bg-white border border-slate-200 rounded-2xl p-4 hover:border-[#E05638] transition"
                data-testid={`admin-kpi-${slug(c.label)}`}>
            <c.icon size={16} className={c.color}/>
            <div className="mt-2 font-heading text-2xl font-extrabold">{c.value}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-0.5">{c.label}</div>
          </Link>
        ))}
      </div>

      <h2 className="mt-8 text-sm font-bold uppercase tracking-widest text-slate-500">Revenu récurrent</h2>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-[#E05638] to-orange-500 text-white rounded-2xl p-4" data-testid="admin-kpi-mrr">
          <Repeat size={16} className="text-white/80"/>
          <div className="mt-2 font-heading text-3xl font-extrabold">{fmtEur(s.mrr || 0)}</div>
          <div className="text-[10px] uppercase tracking-widest text-white/80 font-bold mt-0.5">MRR</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="admin-kpi-arr">
          <Euro size={16} className="text-emerald-600"/>
          <div className="mt-2 font-heading text-2xl font-extrabold">{fmtEur(s.arr || 0)}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-0.5">ARR</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="admin-kpi-active-subs">
          <UserCheck size={16} className="text-blue-600"/>
          <div className="mt-2 font-heading text-2xl font-extrabold">{s.active_subscribers || 0}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-0.5">Abonnés actifs</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="admin-kpi-payments-30d">
          <CreditCard size={16} className="text-purple-600"/>
          <div className="mt-2 font-heading text-2xl font-extrabold">{s.payments_last_30d || 0}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-0.5">Paiements 30 j</div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        {(s.subscribers_by_plan || []).map((p) => (
          <div key={p.key} className={`rounded-2xl border p-4 flex items-center justify-between gap-3 ${p.key === "premium" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white"}`}
               data-testid={`admin-subs-${p.key}`}>
            <div>
              <div className={`text-[10px] uppercase tracking-widest font-bold ${p.key === "premium" ? "text-white/70" : "text-slate-500"}`}>{p.name}</div>
              <div className="font-heading text-2xl font-extrabold mt-1">{p.count} abonné{p.count > 1 ? "s" : ""}</div>
              <div className={`text-xs mt-0.5 ${p.key === "premium" ? "text-white/70" : "text-slate-500"}`}>{p.price.toFixed(2).replace(".", ",")} € / mois</div>
            </div>
            <div className="text-right">
              <div className={`text-[10px] uppercase tracking-widest font-bold ${p.key === "premium" ? "text-white/70" : "text-slate-500"}`}>Revenu</div>
              <div className="font-heading text-xl font-extrabold">{fmtEur(p.revenue)}</div>
            </div>
          </div>
        ))}
      </div>
      {s.canceling_subscribers > 0 && (
        <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 inline-block" data-testid="admin-canceling-warning">
          ⚠️ {s.canceling_subscribers} résiliation(s) programmée(s) à la fin de la période en cours.
        </div>
      )}

      {inv && inv.sent > 0 && (
        <div data-testid="admin-invites-conversion">
          <h2 className="mt-8 text-sm font-bold uppercase tracking-widest text-slate-500">Invitations plateforme</h2>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="admin-invites-sent">
              <Send size={16} className="text-blue-600"/>
              <div className="mt-2 font-heading text-2xl font-extrabold">{inv.sent}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-0.5">Envoyées</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="admin-invites-accepted">
              <UserCheck size={16} className="text-emerald-600"/>
              <div className="mt-2 font-heading text-2xl font-extrabold">{inv.accepted}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-0.5">Inscriptions</div>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-2xl p-4" data-testid="admin-invites-rate">
              <TrendingUp size={16} className="text-white/80"/>
              <div className="mt-2 font-heading text-3xl font-extrabold">{inv.rate}%</div>
              <div className="text-[10px] uppercase tracking-widest text-white/80 font-bold mt-0.5">Taux de conversion</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="admin-invites-pending">
              <Clock size={16} className="text-amber-600"/>
              <div className="mt-2 font-heading text-2xl font-extrabold">{inv.pending}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-0.5">En attente</div>
            </div>
          </div>
          {(inv.by_context || []).length > 0 && (
            <div className="mt-3 bg-white border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100">
              {inv.by_context.map((c) => (
                <div key={c.context} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`admin-invites-ctx-${slug(c.context)}`}>
                  <div className="text-sm font-semibold text-slate-700">{c.label}</div>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span><b className="text-slate-800">{c.sent}</b> envoyées</span>
                    <span><b className="text-emerald-700">{c.accepted}</b> inscrites</span>
                    <span className="font-bold text-emerald-700">{c.rate}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <h2 className="mt-8 text-sm font-bold uppercase tracking-widest text-slate-500">Essais se terminant bientôt</h2>
      <div className="mt-3 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admin-trials-ending">
        {trials.length === 0 ? (
          <div className="p-5 text-sm text-slate-500" data-testid="admin-trials-empty">Aucun essai gratuit en cours arrivant à échéance.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {trials.map((t) => (
              <div key={t.user_id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`admin-trial-row-${t.user_id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{t.business_name || t.contact_name || t.email}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {t.contact_name && <span>{t.contact_name} · </span>}
                    {t.email}{t.phone ? ` · ${t.phone}` : ""}{t.city ? ` · ${t.city}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {t.reminder_sent && (
                    <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Relancé</span>
                  )}
                  <span className={`text-xs font-bold rounded-full px-2.5 py-1 ${t.days_left <= 3 ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                    J-{t.days_left}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <h2 className="mt-8 text-sm font-bold uppercase tracking-widest text-slate-500">Vue d'ensemble</h2>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {global.map((c) => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-2xl p-4"
               data-testid={`admin-global-${slug(c.label)}`}>
            <c.icon size={16} className="text-[#E05638]"/>
            <div className="mt-2 font-heading text-xl font-extrabold">{c.value}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="font-heading text-lg font-bold mb-3">Nouveaux inscrits — 30 derniers jours</div>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={s.signup_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
                <XAxis dataKey="date" fontSize={10}/>
                <YAxis fontSize={10}/>
                <Tooltip/>
                <Line type="monotone" dataKey="count" stroke="#E05638" strokeWidth={2}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <AdminAlertsStats />
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="admin-chart-categories">
          <div className="font-heading text-lg font-bold mb-3">Offres publiées par catégorie</div>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={s.by_category || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
                <XAxis dataKey="name" fontSize={10} angle={-20} textAnchor="end" height={50}/>
                <YAxis fontSize={10}/>
                <Tooltip/>
                <Bar dataKey="count" fill="#E05638" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="admin-chart-cities">
          <div className="font-heading text-lg font-bold mb-3">Offres publiées par ville</div>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={s.by_city || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
                <XAxis dataKey="name" fontSize={10} angle={-20} textAnchor="end" height={50}/>
                <YAxis fontSize={10}/>
                <Tooltip/>
                <Bar dataKey="count" fill="#2C6E7A" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
